import React, { memo } from 'react';
import { Blok } from '../../../interfaces/Blok.interface';
import { Accordion } from 'react-accessible-accordion';
import { PreNavItem } from '../PreNav/PreNav.interface';
import { MainNavLink } from '../MainNav/MainNav.interface';
import { LinkProps } from '../../../interfaces/LinkProps.interface';
import { withBlokWrapper } from '../../BlokWrapper/BlokWrapper';
import { Theme } from '../../../interfaces/enums/Theme.enum';
import MainNavAccordionItem from './MainNavAccordionItem';
import PreNavAccordionItem from './PreNavAccordionItem';
import SideNavButtons from './SideNavButtons';
import CustomLink from '../../CustomLink/CustomLink';
import { SideNavStatus, VisibleSideNavType } from '../Header';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { getAlgoliaPermittedContentTypesFilter } from '../../../utils/getAlgoliaPermittedContentTypesFilter/getAlgoliaPermittedContentTypesFilter.util';

interface SideNavProps {
  pre_navigation_items: PreNavItem[];
  main_navigation_items: MainNavLink[];
  mobile_primary_cta: Blok[];
  mobile_secondary_cta: Blok[];
  secondary_cta: LinkProps[];
  sideNavStatus: SideNavStatus;
  search_heading: string;
  search?: JSX.Element;
  closeSideNav: () => void;
  searchIndexName: string;
  overriddenSearchClient: any;
  searchPermittedContentTypes: string[];
}

const SideNav: React.FC<SideNavProps> = ({
  pre_navigation_items,
  main_navigation_items,
  sideNavStatus,
  mobile_primary_cta,
  mobile_secondary_cta,
  secondary_cta,
  search_heading,
  search = undefined,
  closeSideNav,
  searchIndexName,
  overriddenSearchClient,
  searchPermittedContentTypes,
}) => {
  return (
    <div
      className={`flex transition-all duration-[500ms] ease-in-out fixed h-[100dvh] w-screen top-0 z-50 xl:hidden  ${
        sideNavStatus.isVisible ? 'right-0' : '-right-full'
      }`}
    >
      <button
        className="flex-1"
        aria-label="close"
        onClick={closeSideNav}
      ></button>
      <nav
        className={`bg-[white] flex flex-col justify-between text-text/body overflow-hidden h-full transition-all duration-[500ms] ${
          sideNavStatus.isVisible
            ? 'w-full sm:w-[500px] sm:border-l sm:border-l-border/light'
            : 'w-0'
        }`}
      >
        <div className="py-5 px-6 bg-surface/background">
          <div className="flex justify-between items-center">
            <p className="font-dm-serif-display text-2xl leading-[0]">
              {sideNavStatus.sideNavType === VisibleSideNavType.Menu
                ? 'Menu'
                : search_heading}
            </p>
            <button
              className="h-[24px] w-[24px] bg-[url('/svgs/close.svg')] bg-no-repeat"
              aria-label="close menu"
              onClick={closeSideNav}
            />
          </div>
        </div>

        {sideNavStatus.sideNavType === VisibleSideNavType.Menu && (
          <>
            <div className="overflow-scroll grow flex-1">
              <Accordion allowZeroExpanded>
                <div className="py-6 space-y-2">
                  {!!main_navigation_items.length &&
                    main_navigation_items.map((item) => (
                      <div className="flex justify-between" key={item._uid}>
                        {!!item?.dropdown?.length ? (
                          <MainNavAccordionItem {...item} />
                        ) : (
                          <CustomLink
                            url={item?.link?.cached_url}
                            label={item?.label}
                            className={`nav-link w-full text-lg font-bold px-6 py-2 transition-all block text-text/body hover:text-text/body-accent`}
                          >
                            {item?.label}
                          </CustomLink>
                        )}
                      </div>
                    ))}
                  {!!pre_navigation_items.length &&
                    pre_navigation_items.map((item) => (
                      <div className="flex justify-between" key={item._uid}>
                        {!!item?.dropdown?.length ? (
                          <PreNavAccordionItem {...item} />
                        ) : (
                          <CustomLink
                            url={item?.link?.cached_url}
                            label={item?.label}
                            className={`nav-link w-full text-lg font-bold px-6 py-2 transition-all block text-text/body hover:text-text/body-accent`}
                          >
                            {item?.label}
                          </CustomLink>
                        )}
                      </div>
                    ))}
                </div>
              </Accordion>
            </div>
            <SideNavButtons
              primary_cta={mobile_primary_cta}
              secondary_cta={mobile_secondary_cta}
            />
          </>
        )}

        {/*-- search --*/}
        {search && sideNavStatus.sideNavType === VisibleSideNavType.Search && (
          <div className="overflow-scroll grow p-6">
            <InstantSearch
              indexName={searchIndexName}
              searchClient={overriddenSearchClient}
            >
              <Configure
                filters={`${getAlgoliaPermittedContentTypesFilter(
                  searchPermittedContentTypes
                )}`}
                hitsPerPage={3}
              />
              {search}
            </InstantSearch>
          </div>
        )}
      </nav>
    </div>
  );
};

export default memo(withBlokWrapper(SideNav, Theme.ALT));
